import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { providersAPI } from '../../services/api';
import ConfirmDialog from '../../components/ConfirmDialog';
import { Provider } from '../../types';

interface ProviderFormData {
  providerName: string;
  isActive: boolean;
  displayOrder: number;
}

const Providers = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProvider, setEditingProvider] = useState<Provider | null>(null);
  const [formData, setFormData] = useState<ProviderFormData>({
    providerName: '',
    isActive: true,
    displayOrder: 0,
  });
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; id: number }>({ open: false, id: 0 });

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      const response = await providersAPI.getAll();
      setProviders(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (provider: Provider | null = null) => {
    if (provider) {
      setEditingProvider(provider);
      setFormData({
        providerName: provider.ProviderName,
        isActive: !!provider.IsActive,
        displayOrder: provider.DisplayOrder,
      });
    } else {
      setEditingProvider(null);
      setFormData({ providerName: '', isActive: true, displayOrder: 0 });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProvider(null);
    setFormData({ providerName: '', isActive: true, displayOrder: 0 });
  };

  const handleSubmit = async () => {
    try {
      if (editingProvider) {
        await providersAPI.update(editingProvider.ProviderId, formData);
      } else {
        await providersAPI.create(formData);
      }
      fetchProviders();
      handleCloseDialog();
      setError('');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || 'Failed to save provider');
    }
  };

  const handleDeleteClick = (id: number) => {
    setConfirmDialog({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    const id = confirmDialog.id;
    setConfirmDialog({ open: false, id: 0 });
    try {
      await providersAPI.delete(id);
      fetchProviders();
      setError('');
    } catch (err) {
      setError('Failed to delete provider');
    }
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Providers</Typography>
        <Button
          variant="contained"
          color="secondary"
          size="small"
          startIcon={<AddIcon fontSize="small" />}
          onClick={() => handleOpenDialog()}
        >
          Add Provider
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Provider Name</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Display Order</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {providers.map((provider) => (
              <TableRow key={provider.ProviderId}>
                <TableCell>{provider.ProviderName}</TableCell>
                <TableCell>{provider.IsActive ? 'Yes' : 'No'}</TableCell>
                <TableCell>{provider.DisplayOrder}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleOpenDialog(provider)} size="small">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDeleteClick(provider.ProviderId)}
                    size="small"
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingProvider ? 'Edit Provider' : 'Add Provider'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            size="small"
            label="Provider Name"
            fullWidth
            value={formData.providerName}
            onChange={(e) => setFormData({ ...formData, providerName: e.target.value })}
            required
          />
          <TextField
            margin="dense"
            size="small"
            label="Display Order"
            type="number"
            fullWidth
            value={formData.displayOrder}
            onChange={(e) =>
              setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} size="small">Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="secondary"
            size="small"
            disabled={!formData.providerName}
          >
            {editingProvider ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.open}
        title="Deactivate Provider"
        message="Are you sure you want to deactivate this provider?"
        confirmLabel="Deactivate"
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDialog({ open: false, id: 0 })}
      />
    </Box>
  );
};

export default Providers;
